import React from 'react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import {Alert, Space, Tag} from 'antd';
import InvoiceStatusMark from '../../components/pages/InvoicesPage/InvoiceStatusMark';
import StatusLine from '../../components/StatusLine';
import PrimaryTextButton from '../../components/buttons/PrimaryTextButton';
import IconWrapper from '../../components/IconWrapper';
import InvoicePreviewWithIcon from '../../components/pages/NetSuiteJournalEntriesPage/InvoicePreviewWithIcon';
import {
  AddInvoiceIcon,
  FileInvoiceIcon,
  Invoice2Icon,
  InvoicePendingIcon,
  InvoiceSuggestIcon,
  TransactionDeclinedIcon,
} from '../../icons';
import {objectHelpers} from './object.helpers';
import {OptionsList} from './form.helpers';
import {reviewHelpers} from './review.helpers';
import {
  AMOUNT_TYPE,
  expenseTypesConstants,
  invoiceApprovalStatusConstants,
  invoiceMatchTypeConstants,
  invoicePaymentStatusConstants,
  invoiceStatusConstants,
  invoiceTypeConstants,
  NO_INVOICE_TAG_FILTER_VALUE
} from '../../constants';
import routes from '../../routes/routes.json';

const gObjProp = objectHelpers.getObjProp;

const {MATCHED, PENDING} = invoiceStatusConstants;

const {AUTOMATED} = invoiceMatchTypeConstants;

// Kept in step with DATE_WINDOW_DAYS in app-backend match_invoice/strategies/default.py, so the
// picker's default range and the backend's candidate window agree.
const MATCH_DATE_WINDOW_DAYS = 60;

const invoiceApprovalStatuses = {
  [invoiceApprovalStatusConstants.ASSIGNED]: {
    color: '#00465D',
    backgroundColor: '#C6E7F2',
    key: 'invoiceApprovalStatuses.assigned'
  },
  [invoiceApprovalStatusConstants.APPROVED]: {
    color: '#245F07',
    backgroundColor: '#D6FFCE',
    key: 'invoiceApprovalStatuses.approved'
  },
  [invoiceApprovalStatusConstants.PENDING]: {
    color: '#7A2E0E',
    backgroundColor: '#FEF0C7',
    key: 'invoiceApprovalStatuses.inReview'
  },
  [invoiceApprovalStatusConstants.REJECTED]: {
    color: '#D92D20',
    backgroundColor: '#FEE4E2',
    key: 'invoiceApprovalStatuses.rejected'
  },
  [invoiceApprovalStatusConstants.UNASSIGNED]: {
    color: '#9CA1AA',
    backgroundColor: '#F3F3F3',
    key: 'invoiceApprovalStatuses.unassigned'
  },
};

const invoicePaymentStatuses = {
  [invoicePaymentStatusConstants.DUE]: {
    key: 'invoiceStatuses.due',
    color: '#FFCC00'
  },
  [invoicePaymentStatusConstants.UNMATCHED]: {
    key: 'invoiceStatuses.unmatched',
    color: '#FA8C16'
  },
  [invoicePaymentStatusConstants.OVERDUE]: {
    key: 'invoiceStatuses.overdue',
    color: '#FF4D4F'
  },
  [invoicePaymentStatusConstants.PAID]: {
    key: 'invoiceStatuses.paid',
    color: '#52C41A'
  },
  [invoicePaymentStatusConstants.PAID_OUTSIDE]: {
    key: 'invoiceStatuses.paid',
    color: '#52C41A'
  },
  [invoicePaymentStatusConstants.PROCESSING]: {
    key: 'invoiceStatuses.processing',
    color: '#CDD0D5'
  },
  [invoicePaymentStatusConstants.UPCOMING]: {
    key: 'invoiceStatuses.upcoming',
    color: '#CDD0D5'
  },
};

/**
 * Returns invoice payment status options, optionally excluding PAID_OUTSIDE and PROCESSING statuses.
 * @param {Object} [params={}] - Parameters object.
 * @param {boolean} [params.excludePaidOutsideProcessing=false] - When true, removes PAID_OUTSIDE and PROCESSING from the list.
 * @returns {Array<{value: string, label: string, image: React.ReactElement}>} Payment status options with status line images.
 * @example
 * const options = InvoicePaymentStatusesOptions({ excludePaidOutsideProcessing: true });
 */
export const InvoicePaymentStatusesOptions = ({ excludePaidOutsideProcessing = false } = {}) => {
  const statuses = Object.keys(invoicePaymentStatuses).reduce((acc, value) => {
    if (
      excludePaidOutsideProcessing &&
      (String(value) === String(invoicePaymentStatusConstants.PAID_OUTSIDE) ||
       String(value) === String(invoicePaymentStatusConstants.PROCESSING))
    ) {
      return acc;
    }
    acc[value] = invoicePaymentStatuses[value]?.key;
    return acc;
  }, {});
  return OptionsList(statuses).map(option => ({
    ...option,
    image: <StatusLine color={invoicePaymentStatuses[option.value]?.color} />
  }));
};

// Icons used to represent an invoice document type (Invoice / Receipt).
const invoiceDocumentTypeIcons = {
  [invoiceTypeConstants.INVOICE]: <FileInvoiceIcon />,
  [invoiceTypeConstants.RECEIPT]: <Invoice2Icon />,
};

// Document types selectable in the invoice filters.
export const invoiceDocumentTypeFilterValues = [invoiceTypeConstants.INVOICE, invoiceTypeConstants.RECEIPT];

// Payment statuses selectable in the invoice filters.
export const invoicePaymentStatusFilterValues = [
  invoicePaymentStatusConstants.UPCOMING,
  invoicePaymentStatusConstants.DUE,
  invoicePaymentStatusConstants.OVERDUE,
  invoicePaymentStatusConstants.UNMATCHED,
  invoicePaymentStatusConstants.PROCESSING,
  invoicePaymentStatusConstants.PAID,
];

// Approval statuses selectable in the invoice filters. Filters on the raw approval_status value, which
// is also what the column tag shows — see CONTEXT.md's "Approval Status filter" glossary entry and
// docs/adr/0014-approval-status-filter-matches-raw-enum-not-display-fold.md.
export const invoiceApprovalStatusFilterValues = [
  invoiceApprovalStatusConstants.UNASSIGNED,
  invoiceApprovalStatusConstants.ASSIGNED,
  invoiceApprovalStatusConstants.PENDING,
  invoiceApprovalStatusConstants.APPROVED,
  invoiceApprovalStatusConstants.REJECTED,
];


/**
 * Collection of invoice display, status, filtering, and navigation utility helpers.
 * @example
 * invoicesHelpers.getInvoiceVendorName(invoice);
 */
export const invoicesHelpers = {
  /**
   * Converts a minimal invoice object into a expense-shaped object for display in expense lists.
   * @param {Object|null} invoice - Invoice object with amount, date, and vendor.
   * @returns {{amount: number, created_date: string, source: string}|null} Expense-shaped object or null.
   * @example
   * invoicesHelpers.convertInvoiceToExpense({ amount: 100, date: '2024-03-01', vendor: 'Acme' });
   */
  convertInvoiceToExpense: (invoice) => {
    if (!invoice) return null;

    return {
      price: invoice?.amount || 0,
      expense_date: invoice?.date,
      vendor: invoice?.vendor
    }
  },
  /**
   * Converts UI filter form values (amountType, dateRange, etc.) into API query parameter format.
   * @param {Object} input - Filter form values.
   * @param {Object} [options={}] - Options allowing date/amount key name overrides.
   * @param {string} [options.dateGteKey='date_gte'] - API key for the start date filter.
   * @param {string} [options.dateLteKey='date_lte'] - API key for the end date filter.
   * @param {string} [options.amountLteKey='amount_lte'] - API key for the upper amount bound.
   * @param {string} [options.amountGteKey='amount_gte'] - API key for the lower amount bound.
   * @returns {Object} Mapped API query parameter object.
   * @example
   * invoicesHelpers.convertMatchFilterPropsToApi({ amountType: 'max', amount: 100, dateRange: [start, end] });
   */
  convertMatchFilterPropsToApi: (input, { dateGteKey = 'date_gte', dateLteKey = 'date_lte', amountLteKey = 'amount_lte', amountGteKey = 'amount_gte' } = {}) => {
    const output = {};
    const dateFormat = 'YYYY-MM-DD';

    // Convert amountType
    if (input.amountType && input.amount) {
      const {amount, amountType} = input;
      const amountMapping = {
        [AMOUNT_TYPE.MIN]: amountLteKey,
        [AMOUNT_TYPE.MAX]: amountGteKey
      };
      if (amountMapping[amountType]) {
        output[amountMapping[amountType]] = amount;
      } else if (amountType === '=') {
        output[amountLteKey] = amount;
        output[amountGteKey] = amount;
      }
    }

    // Convert dateRange (use configurable keys, defaulting to date_gte/date_lte)
    if (input.dateRange && Array.isArray(input.dateRange) && input.dateRange.length === 2) {
      output[dateGteKey] = input.dateRange[0].format(dateFormat);
      output[dateLteKey] = input.dateRange[1].format(dateFormat);
    }

    // Add other input values as they are, excluding amountType and dateRange
    Object.keys(input).forEach(key => {
      if (!['amount', 'amountType', 'dateRange'].includes(key)) {
        output[key] = input[key];
      }
    });

    return output;
  },

  /**
   * Converts the invoice tag filter selection into the API's `invoice_tags` and `untagged` params.
   * @param {Object} [query={}] - Query holding `invoice_tags`, which may include the "no tag" value.
   * @returns {Object} The query with `invoice_tags` limited to real tag names and `untagged` set when "no tag" was chosen.
   * @example
   * invoicesHelpers.getInvoiceTagQuery({ invoice_tags: ['URGENT', '__no_tag__'] }); // { invoice_tags: ['URGENT'], untagged: true }
   */
  getInvoiceTagQuery: ({invoice_tags: selected, ...rest} = {}) => {
    // A single value restored from the URL arrives as a bare string or number, not an array.
    const tags = (Array.isArray(selected) ? selected : [selected])
      .filter(tag => tag != null && tag !== '')
      .map(String);
    const names = tags.filter(tag => tag !== NO_INVOICE_TAG_FILTER_VALUE);
    return {
      ...rest,
      ...(names.length ? {invoice_tags: names} : {}),
      ...(tags.includes(NO_INVOICE_TAG_FILTER_VALUE) ? {untagged: true} : {}),
    };
  },
  /**
   * Returns the sort parameters for invoice queries, switching to amount-based ordering when an amount filter is active.
   * @param {Object|null} query - API query object with optional amount_gte or amount_lte filters.
   * @returns {{sort_by: string, sort_order: string}} Sort parameters object.
   * @example
   * invoicesHelpers.getInvoiceOrdering({ amount_gte: 100 }); // { sort_by: 'amount', sort_order: 'desc' }
   * invoicesHelpers.getInvoiceOrdering({ search: 'acme' }); // { sort_by: 'date', sort_order: 'desc' }
   */
  getInvoiceOrdering: (query) => {
    if (query?.amount_gte != null || query?.amount_lte != null) return { sort_by: 'amount', sort_order: 'desc' };
    return { sort_by: 'date', sort_order: 'desc' };
  },
  /**
   * Returns true when an invoice has parse errors or has been pending for more than 3 days.
   * @param {Object} invoice - Invoice object with has_parse_errors, status, and created_at properties.
   * @returns {boolean} True if the invoice has a parse error condition.
   * @example
   * invoicesHelpers.hasInvoiceParseError({ has_parse_errors: true });
   */
  hasInvoiceParseError: (invoice) => {
    const threeDaysAgoDate = dayjs().subtract(3, 'days').startOf('day');

    if (gObjProp(invoice, 'has_parse_errors') === true) {
      return true;
    }

    if (gObjProp(invoice, 'status') === PENDING) {
      const createdAt = gObjProp(invoice, 'created_at');
      return dayjs(createdAt).startOf('day').isBefore(threeDaysAgoDate);
    }

    return false;
  },
  /**
   * Returns a div element with the appropriate match-type icon based on the invoice's status and match type.
   * @param {Object} invoice - Invoice object with match_type, status, and parse error flags.
   * @returns {React.ReactElement} Div element containing the relevant icon.
   * @example
   * invoicesHelpers.getInvoiceMatchType(invoice);
   */
  getInvoiceMatchType: (invoice) => {
    const matchType = gObjProp(invoice, 'match_type');
    const status = gObjProp(invoice, 'status');
    const hasParseErrors = invoicesHelpers.hasInvoiceParseError(invoice);

    if (hasParseErrors) {
      return <div className='invoice-match'><TransactionDeclinedIcon /></div>;
    }

    let matchElement;

    switch (status) {
      case MATCHED:
        matchElement = matchType === AUTOMATED ? <InvoiceSuggestIcon /> : <Invoice2Icon />;
        break;
      case PENDING:
        matchElement = <InvoicePendingIcon />;
        break;
      default:
        matchElement = matchType === AUTOMATED ? <InvoiceSuggestIcon /> : <AddInvoiceIcon />;
        break;
    }

    return <div className='invoice-match'>{matchElement}</div>;
  },
  /**
   * Returns the invoice payment status object for the given status key.
   * @param {string} status - Invoice payment status constant.
   * @returns {{key: string, color: string}} Status object with translation key and color.
   * @example
   * invoicesHelpers.getInvoicePaymentStatus('paid');
   */
  getInvoicePaymentStatus: (status) => objectHelpers.getStatus(status, invoicePaymentStatuses),

  /**
   * Returns an InvoiceStatusMark component for the given status, or an empty string if status is null/undefined.
   * @param {string|null|undefined} status - Invoice status code.
   * @returns {React.ReactElement|string} InvoiceStatusMark component or empty string.
   * @example
   * invoicesHelpers.getInvoiceStatusMark('paid');
   */
  getInvoiceStatusMark: (status) => objectHelpers.isNaV(status) ? '' : <InvoiceStatusMark statusCode={status} className='invoice-status-mark' />,

  /**
   * Returns the icon element for an invoice document type, defaulting to the Invoice icon.
   * @param {string} type - Invoice document type (invoiceTypeConstants).
   * @returns {React.ReactElement} Icon element for the document type.
   * @example
   * invoicesHelpers.getInvoiceDocumentTypeIcon('RECEIPT');
   */
  getInvoiceDocumentTypeIcon: (type) => invoiceDocumentTypeIcons[type] ?? invoiceDocumentTypeIcons[invoiceTypeConstants.INVOICE],

  /**
   * Returns a document preview element with the icon and label matching the invoice document type (Invoice or Receipt).
   * @param {Object|null} invoice - Invoice object with id and invoice_type.
   * @param {Function} t - i18n translation function.
   * @returns {React.ReactElement|string} Preview element with type icon and label, or empty string when no invoice id.
   * @example
   * invoicesHelpers.getInvoiceDocumentPreview(invoice, t);
   */
  getInvoiceDocumentPreview: (invoice, t) => {
    if (!invoice?.id) return '';
    const type = invoice?.type === invoiceTypeConstants.RECEIPT
      ? invoiceTypeConstants.RECEIPT
      : invoiceTypeConstants.INVOICE;
    return (
      <Space className='d-flex d-flex-child' size='small'>
        <InvoicePreviewWithIcon
          invoiceId={invoice.id}
          variant='backend'
          icon={<IconWrapper size={16}>{invoiceDocumentTypeIcons[type]}</IconWrapper>}
        />
        <span>{t?.(`invoices:types.${type}`)}</span>
      </Space>
    );
  },

  /**
   * Returns a styled Tag element for the invoice's approval_status, exactly as the API returns it.
   * @param {Object|null} invoice - Invoice object with an approval_status property.
   * @param {Function} t - i18n translation function.
   * @returns {React.ReactElement|null} Tag element or null if no invoice.
   * @example
   * invoicesHelpers.getInvoiceApprovalStatusTag(invoice, t);
   */
  getInvoiceApprovalStatusTag: (invoice, t) => {
    if (!invoice) return null;
    return invoicesHelpers.getInvoiceApprovalStatusTagForValue(invoice?.approval_status, t);
  },

  /**
   * Returns the same styled Tag element as getInvoiceApprovalStatusTag, but for a raw approval_status
   * value directly. Used where only the status value is available (e.g. building filter choices),
   * not a full invoice record.
   * @param {string} status - Invoice approval status constant.
   * @param {Function} t - i18n translation function.
   * @returns {React.ReactElement|null} Tag element or null if no status.
   * @example
   * invoicesHelpers.getInvoiceApprovalStatusTagForValue('APPROVED', t);
   */
  getInvoiceApprovalStatusTagForValue: (status, t) =>
    reviewHelpers.getStatusLabel({value: status, statuses: invoiceApprovalStatuses, t}),

  /**
   * Returns a styled Tag element displaying the invoice source type, or empty string if no value.
   * @param {string} value - Source type label string.
   * @returns {React.ReactElement|string} Tag element or empty string.
   * @example
   * invoicesHelpers.getInvoiceSourceTypeTag('email');
   */
  getInvoiceSourceTypeTag: (value) => {
    return value
      ? (
        <Tag style={{
          backgroundColor: '#F3F3F4',
          borderColor: '#CDD0D5',
          borderRadius: 2,
          color: '#9CA1AA',
          marginRight: 0
        }}>{value}</Tag>
      )
      : '';
  },

  /**
   * Returns true when the invoice payment status represents a settled state (PAID, PAID_OUTSIDE, or PROCESSING).
   * @param {string} status - Invoice payment status constant.
   * @returns {boolean} True if the invoice is considered settled.
   * @example
   * invoicesHelpers.isInvoiceSettled('paid'); // true
   */
  isInvoiceSettled: (status) => {
    return [
      invoicePaymentStatusConstants.PAID,
      invoicePaymentStatusConstants.PAID_OUTSIDE,
      invoicePaymentStatusConstants.PROCESSING
    ].includes(status);
  },

  /**
   * Returns true when the invoice payment status is PAID or PAID_OUTSIDE.
   * Use this to determine whether an invoice has already been settled through a direct payment,
   * as opposed to PROCESSING (payment in flight) which is not yet fully completed.
   * @param {Object|null|undefined} invoice - Invoice object with a payment_status property.
   * @returns {boolean} True if the invoice payment status is PAID or PAID_OUTSIDE.
   * @example
   * invoicesHelpers.isInvoicePaid({ payment_status: 'PAID' }); // true
   * invoicesHelpers.isInvoicePaid({ payment_status: 'PAID_OUTSIDE' }); // true
   * invoicesHelpers.isInvoicePaid({ payment_status: 'PROCESSING' }); // false
   */
  isInvoicePaid: (invoice) => [
    invoicePaymentStatusConstants.PAID,
    invoicePaymentStatusConstants.PAID_OUTSIDE,
  ].includes(invoice?.payment_status),

  /**
   * Returns the invoice's candidate match date window: the earlier of due_date/date minus the
   * match window, to the later of the two plus it. Both the issue date and the due date are used
   * as anchors — an invoice on terms longer than the window may have been paid on receipt, and
   * anchoring only on the due date would put that payment outside the range (H1MVP-4838).
   * @param {Object|null|undefined} invoice - Invoice object with due_date and/or date properties.
   * @returns {[dayjs.Dayjs, dayjs.Dayjs]|null} A [start, end] dayjs range, or null if neither date is set.
   * @example
   * invoicesHelpers.getInvoiceMatchDateRange({ due_date: '2024-03-01', date: '2024-02-01' });
   */
  getInvoiceMatchDateRange: (invoice) => {
    const anchors = [invoice?.due_date, invoice?.date].filter(Boolean).map(date => dayjs(date));
    if (!anchors.length) return null;

    const earliest = anchors.reduce((a, b) => (a.isBefore(b) ? a : b));
    const latest = anchors.reduce((a, b) => (a.isAfter(b) ? a : b));

    return [earliest.subtract(MATCH_DATE_WINDOW_DAYS, 'day'), latest.add(MATCH_DATE_WINDOW_DAYS, 'day')];
  },

  /**
   * Returns the default expense-matching filter form values for an invoice (date range and
   * amount), or {} for an already-paid invoice, which keeps the unfiltered candidate list.
   *
   * `skipPaidCheck` exists for callers that know the invoice's `payment_status` is stale — e.g.
   * right after a match is removed, the invoice object still reads PAID (settled via the
   * just-removed transaction) until it's refetched, which would otherwise wrongly suppress the
   * prefill right when it's most useful for re-finding a match.
   * @param {Object|null|undefined} invoice - Invoice object with due_date, date, amount, payment_status.
   * @param {Object} [options={}]
   * @param {boolean} [options.skipPaidCheck=false] - Skip the isInvoicePaid gate.
   * @returns {Object} Filter form values suitable for invoicesHelpers.convertMatchFilterPropsToApi.
   * @example
   * invoicesHelpers.getPrefilledMatchFilter({ due_date: '2024-03-01', amount: 100 });
   */
  getPrefilledMatchFilter: (invoice, {skipPaidCheck = false} = {}) => {
    if (!skipPaidCheck && invoicesHelpers.isInvoicePaid(invoice)) return {};

    const dateRange = invoicesHelpers.getInvoiceMatchDateRange(invoice);

    return {
      ...(dateRange ? {dateRange} : {}),
      // > 0, not != null: a zero (or negative) amount would still pass through
      // convertMatchFilterPropsToApi ("0" is a truthy string) as high_value: 0, which the backend
      // rejects with "high_value must be a positive value".
      ...(invoice?.amount > 0 ? {amount: invoice.amount.toString(), amountType: AMOUNT_TYPE.MAX} : {}),
    };
  },

  /**
   * Returns true when the expense has the INVOICE expense type.
   * @param {Object|null|undefined} expense - Expense object with an expense_type property.
   * @returns {boolean} True if the expense type is INVOICE.
   * @example
   * invoicesHelpers.isExpenseInvoiceType({ expense_type: 'invoice' }); // true
   */
  isExpenseInvoiceType: (expense) => expense?.expense_type === expenseTypesConstants.INVOICE,

  /**
   * Returns a truthy value when the invoice has an associated accounting invoice ID.
   * @param {Object|null|undefined} invoice - Invoice object with an optional accounting_invoice_id.
   * @returns {string|number|null|undefined} The accounting_invoice_id value if present and invoice is truthy, otherwise falsy.
   * @example
   * invoicesHelpers.isAccountingInvoice({ accounting_invoice_id: 'acc_789' }); // 'acc_789'
   */
  isAccountingInvoice: (invoice) => invoice && invoice?.accounting_invoice_id,

  /**
   * Returns the route path for the invoice details page for the given invoice ID.
   * @param {string|number|null|undefined} id - Invoice ID.
   * @returns {string} Full path to the invoice details page, or '#' if no ID is provided.
   * @example
   * invoicesHelpers.getInvoiceDetailsLink('inv_123'); // '/invoices/inv_123'
   */
  getInvoiceDetailsLink: (id) => {
    if (!id) return '#';
    return `${routes.invoicesList}/${id}`;
  },

  /**
   * Returns the route path for the invoice payment details section for the given invoice ID.
   * @param {string|number|null|undefined} id - Invoice ID.
   * @returns {string} Full path to the invoice payment anchor, or '#' if no ID is provided.
   * @example
   * invoicesHelpers.getInvoicePaymentDetailsLink('inv_123'); // '/invoices/inv_123#payment'
   */
  getInvoicePaymentDetailsLink: (id) => {
    if (!id) return '#';
    return `${routes.invoicesList}/${id}#payment`;
  },

  /**
   * Returns true when the invoice has not been sent for approval yet: its approval_status is
   * UNASSIGNED (no budget linked) or ASSIGNED (budget linked, payment not requested).
   * @param {Object|null|undefined} invoice - Invoice object with an approval_status property.
   * @returns {boolean} True if no payment request has been made for the invoice.
   * @example
   * invoicesHelpers.isInvoiceNotSentForApproval({ approval_status: 'ASSIGNED' }); // true
   */
  isInvoiceNotSentForApproval: (invoice) => {
    return [
      invoiceApprovalStatusConstants.UNASSIGNED,
      invoiceApprovalStatusConstants.ASSIGNED,
    ].includes(invoice?.approval_status);
  },

  /**
   * Returns true when the invoice is linked to a budget request and is not fully paid through the platform
   * (payment_status is anything but PAID: unpaid, PROCESSING, PAID_OUTSIDE, or missing). Its amount is then treated as already
   * accounted for by the budget request, so it must not be shown or counted as a separate "This invoice" amount.
   * @param {Object|null|undefined} invoice - Invoice object with budget_request_id and payment_status properties.
   * @returns {boolean} True if the invoice has a budget_request_id and payment_status is not PAID.
   * @example
   * invoicesHelpers.isInvoiceAmountCoveredByBudgetRequest({ budget_request_id: 'br_1', payment_status: 'DUE' }); // true
   * invoicesHelpers.isInvoiceAmountCoveredByBudgetRequest({ budget_request_id: 'br_1', payment_status: 'PAID' }); // false
   */
  isInvoiceAmountCoveredByBudgetRequest: (invoice) => {
    return Boolean(invoice?.budget_request_id)
      && invoice?.payment_status !== invoicePaymentStatusConstants.PAID;
  },

  /**
   * Returns the vendor display name for an invoice, preferring the localised name over the generic vendor field.
   * @param {Object|null|undefined} invoice - Invoice object with optional vendor_local_name and vendor properties.
   * @returns {string} Vendor name string, or empty string if no invoice or vendor is available.
   * @example
   * invoicesHelpers.getInvoiceVendorName({ vendor_local_name: 'Acme France', vendor: 'Acme' }); // 'Acme France'
   */
  getInvoiceVendorName: (invoice) => {
    if (!invoice) return '';
    return invoice?.vendor_local_name || invoice?.vendor || '';
  },

  /**
   * Returns true when the invoice status is PENDING (processing state).
   * @param {Object|null|undefined} invoice - Invoice object with a status property.
   * @returns {boolean} True if the invoice status is PENDING.
   * @example
   * invoicesHelpers.isInvoiceProcessing({ status: 'pending' }); // true
   */
  isInvoiceProcessing: (invoice) => invoice?.status === PENDING,

  /**
   * Returns a "Processing..." span when the invoice is in PENDING state, otherwise returns the payment status mark.
   * @param {Object|null|undefined} invoice - Invoice object with status and payment_status properties.
   * @param {Function} t - i18n translation function.
   * @returns {React.ReactElement|string} Processing span or InvoiceStatusMark element.
   * @example
   * invoicesHelpers.getInvoicePaymentStatusDisplay(invoice, t);
   */
  getInvoicePaymentStatusDisplay: (invoice, t) =>
    invoicesHelpers.isInvoiceProcessing(invoice)
      ? <span>{t('processing')}...</span>
      : invoicesHelpers.getInvoiceStatusMark(invoice?.payment_status),

  /**
   * Displays a dismissible toast alert notifying the user that a match was removed, with an undo action.
   * @param {Object} [params={}] - Parameters object.
   * @param {Function} [params.translation] - i18n translation function used for alert text.
   * @param {Function} [params.onClick] - Callback invoked when the user clicks the undo button.
   * @returns {void}
   * @example
   * invoicesHelpers.showMatchRemovedAlert({ translation: t, onClick: () => relinkExpense() });
   */
  showMatchRemovedAlert: ({translation, onClick} = {}) => {
    toast.custom(
      (t) => {
        const onClose = () => toast.dismiss(t.id);

        const onUndo = (e) => {
          onClick?.(e);
          onClose();
        }
        return (
          <Space direction='vertical' style={{width: 400}}>
            <Alert
              message={translation?.('matchRemovedSuccessfully')}
              onClose={onClose}
              type='success'
              closable
              showIcon
              action={(
                <PrimaryTextButton size='small' type='link' className='underline' onClick={onUndo}>
                  {translation?.('undo')}
                </PrimaryTextButton>
              )}
            />
          </Space>
        )
      },
      {duration: 5000}
    );
  }
}
